import React, { useEffect, useMemo, useState, useRef } from 'react'
import { observer } from 'mobx-react'
import {Box, Stack, Text} from '../../../../vanilla'
import { CalendarTabs } from './calendar-tabs'
import { TimeSlots } from './time-slots'
import { useLocation } from 'react-router-dom'
import { useBasketStore, useGlobalStore, usePreBookStore, useRapidDeliveryStore } from '../../../../store/hooks/useStore'
import { slotsMatch, scrollToBooking } from '../../../../utils/booking-utils'
import LoadingOverlay from '../../../../components/loading-overlay'
import SlotsErrorMessaging from './error-messaging'
import { StatusMap } from '../../../../types/commerce'
import { format } from 'date-fns'
import { RapidDeliveryPrompt } from './rapid-delivery-prompt'

export const BookingSlotsContainer = () => {
  const {
    deliverySlots,
    deliverySlotArray,
    currentDeliverySlotId,
    isAppliedPostCodeDeliverable,
    sameDayDeliveryAvailable,
    deliveryPostalCode,
    deliverySlotStatus,
    status
  } = useBasketStore()
  const {getCustomSitePreferenceById} = useGlobalStore()
  const {isRapidDeliveryAvailableForPostcode} = useRapidDeliveryStore()
  const [smallBasketChargeEnabled, setSmallBasketChargeEnabled] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = React.useState<number | undefined>()
  const [hasError, setHasError] = useState(false)
  const [loading, setLoading] = useState(true)

  // Check if we need to focus on Prebook Slot
  const {search} = useLocation()
  const params = new URLSearchParams(search)
  const shouldFocusPrebook = params.get('prebook')
  const {prebookedOrder} = usePreBookStore()
  const preBookSlotID = prebookedOrder?.c_deliverySlotId

  // Scroll to prebooked slot or slot to rebook if reservation expired
  useEffect(() => {
    // Early exit check - still loading || no slots || no ID for rebook || focussing prebook, but no prebook ID
    if (loading || !deliverySlotArray?.length || (!currentDeliverySlotId && !shouldFocusPrebook) || (shouldFocusPrebook && !preBookSlotID)) {
      return
    }

    // Do we want to scroll to prebooked slot, or expired slot
    const focusDeliverySlot: string = shouldFocusPrebook ? preBookSlotID : currentDeliverySlotId
    const clickTab = shouldFocusPrebook ? format(new Date(prebookedOrder?.c_windowStartTime), 'doMMMM') : ''
    deliverySlotArray.forEach((deliverySlot, idx) => {
      for (const slot of deliverySlot.slots || []) {
        if (slotsMatch(slot.id, focusDeliverySlot)) {
          setSelectedTab(idx)
          scrollToBooking(focusDeliverySlot, clickTab)
          break
        }
      }
    })
  }, [deliverySlots, currentDeliverySlotId, loading, preBookSlotID, shouldFocusPrebook])

  const availableDatesText = useMemo(() => {
    let text = ''
    if (deliverySlotArray?.length) {
      const firstDay = deliverySlotArray[0]
      const lastDay = deliverySlotArray[deliverySlotArray.length - 1]
      text = `${firstDay?.day} ${firstDay?.month} - ${lastDay?.day} ${lastDay?.month}`
    }
    return text
  }, [deliverySlotArray])

  useEffect(() => {
    async function fetchChargeEnabled() {
      try {
        const chargeEnabled =  Boolean(await getCustomSitePreferenceById('SmallBasketChargeEnabled'))
        setSmallBasketChargeEnabled(chargeEnabled)
      } catch (error) {
        console.error('Error fetching small basket charge custom preference:', error)
      }
    }

    fetchChargeEnabled()
  }, [])

  const datesAreAvailable = deliverySlots && isAppliedPostCodeDeliverable && availableDatesText.length

  // Check if same day slots are available, if not, select tomorrow
  const initialShowSameDayRef = useRef(true)

  useEffect(() => {
    if (shouldFocusPrebook || currentDeliverySlotId) return

    let mounted = true
    ;(async () => {
      try {
        const deliveryShowSameDayFirst = Boolean(await getCustomSitePreferenceById('deliveryShowSameDayFirst'))
        if (!mounted) return

        if (deliveryShowSameDayFirst && initialShowSameDayRef.current && sameDayDeliveryAvailable) {
          setSelectedTab(0) 
          initialShowSameDayRef.current = false
          return
        }

        // fallback behaviour if pref is false (or not set)
        if (sameDayDeliveryAvailable) {
          setSelectedTab(0)
        } else {
          setSelectedTab(1)
        }
      } catch (err) {
        // fallback to existing behaviour if pref fetch fails
        if (sameDayDeliveryAvailable) {
          setSelectedTab(0)
        } else {
          setSelectedTab(1)
        }
      }
    })()

    return () => {
      mounted = false
    }
  }, [sameDayDeliveryAvailable, shouldFocusPrebook, currentDeliverySlotId, getCustomSitePreferenceById])

  useEffect(() => {
    const requestIsPending = (deliverySlotStatus !== StatusMap.DONE && deliverySlotStatus !== StatusMap.ERROR) || (status === StatusMap.INIT || status === StatusMap.PENDING)
    setHasError(!requestIsPending && deliverySlotStatus === StatusMap.ERROR)
    // Show loading overlay until all data is fetched
    setLoading(requestIsPending)
  }, [deliverySlots, status, deliverySlotStatus])

  // If there is an error fetching delivery slots, show error message
  if (hasError) {
    return <SlotsErrorMessaging error={hasError} />
  }

  // Do not render slots section if postcode is not set
  if (!loading && !deliveryPostalCode && !deliverySlots) {
    return <Stack display="none"></Stack>
  }

  return (
    <Box gridColumn="span 12" paddingBottom="12px" position="relative" minHeight={"450px"} data-test-selector="book-slot-grid">
      <LoadingOverlay containerStyles={{backgroundColor: 'white'}} isLoading={loading} text={"Loading Delivery Slots..."}/>
      {!loading && (
        <>
          {/* Message to show when dates are/aren't available */}
          {datesAreAvailable ? (
            <Text
              style={{color: '#9b9b9b'}}
              variant="text6"
              marginTop={['8px', '20px']}
              paddingX={['16px', '0px']}
            >
              Available times:{' '}
              <Text
                as="span"
                color="black"
                variant={datesAreAvailable ? 'text7' : 'text3'}
              >
                {availableDatesText}
              </Text>
            </Text>
          ) : null}

          {deliverySlots && isAppliedPostCodeDeliverable && deliverySlotArray?.length > 0 ? (
            <>
              <CalendarTabs setSelectedTab={setSelectedTab} selectedTab={selectedTab} />
              <TimeSlots selectedTab={selectedTab} smallBasketEnabled={smallBasketChargeEnabled} />
              {selectedTab === 0 && isRapidDeliveryAvailableForPostcode ? (
                <RapidDeliveryPrompt />
              ) : null}
            </>
          ) :
            // Show error message if there are no delivery slots available
            <SlotsErrorMessaging />
          }
        </>
      )}
    </Box>
  )
}

export const BookingSlots = observer(BookingSlotsContainer)
